import { assert } from "../asserts/index.ts";
import type { PortableDesignerNodeInContext } from "../schemas/importExport/portableDesignerNodeInContextSchema.ts";
import type { DesignerNodeData, NodeSlotPath } from "../schemas/index.ts";
import {
  PORTABLE_DESIGNER_NODE_VERSION,
  type BasicNodeSpec,
  type ExternalNodeContext,
  type NodeSlotPathSegment,
} from "../schemas/index.ts";
import type { GetNodeTypeIdById } from "../state/querying/getNodeTypeIdById.ts";
import { generateDesignerNodeForImportCreate } from "./generateDesignerNodeForImport.ts";
import { nodeImportLookup } from "./nodeImportLookup.ts";
import { preprocessDesignerNodeForImportCreate } from "./preprocessDesignerNodeForImport.ts";
import { renumberClashingTitles } from "./renumberClashingTitles.ts";
import { resolveNodeReferencesByPath } from "./resolveNodeReferencesByPath.ts";
import type { GetNewId } from "./shared.ts";
import type { ImportTarget } from "./types.ts";

export class SlotPathError extends Error {
  name = "SlotPathError";

  constructor() {
    super(`No matching slot paths found.`);
  }
}

export type AmbiguousLinkage = {
  nodeId: number;
  basicNodeSpec: BasicNodeSpec;
  destinationSlotPath: NodeSlotPath;
  context: ExternalNodeContext[];
};

type CreateOptions = {
  addDereferencedNode?: (node: DesignerNodeData) => void;
  onResolveAmbiguousLinkages?: (ambiguousLinkages: AmbiguousLinkage[]) => Promise<[number, number][] | undefined>;
  getNextId: GetNewId;
  migrateSubtree: (node: DesignerNodeData) => DesignerNodeData;
  getNodeTypeId: GetNodeTypeIdById;
  supportedLanguageCodes: Readonly<string[]>;
  /**
   * The names already taken where this is being imported. Supply it and a title in the
   * imported subtree that clashes with one is renumbered rather than repeated (see
   * renumberClashingTitles); leave it out and titles arrive as they were copied.
   */
  getTakenTitles?: () => readonly string[];
};

interface Options {
  slotPathByIdMap: Map<number, NodeSlotPath>;
}

export const importDesignerNodeCreate = ({
  addDereferencedNode,
  onResolveAmbiguousLinkages,
  getNextId,
  migrateSubtree,
  getNodeTypeId,
  supportedLanguageCodes,
  getTakenTitles,
}: CreateOptions) => {
  return async (
    portableSourceNode: PortableDesignerNodeInContext,
    importTarget: ImportTarget,
    { slotPathByIdMap }: Options,
  ) => {
    const { node: originalSubjectNode, linkages, version } = portableSourceNode;
    const { dereferencedNodes: originalDereferenceNodes, externalNodeSlotPaths = [] } = linkages ?? {};
    if (PORTABLE_DESIGNER_NODE_VERSION !== version) {
      // TODO: should be a callback to make nicer ui for this and other validation type errors
      throw new Error(`Unsupported portable node version: ${version}`);
    }

    const dereferencedNodesByOriginalId = new Map<number, DesignerNodeData>();

    const originalNodes = [originalSubjectNode];
    if (originalDereferenceNodes) {
      for (const node of originalDereferenceNodes) {
        originalNodes.push(node);
      }
    }

    const destinationSlotPath = getTargetSlotPathCreate({ getNodeTypeId, slotPathByIdMap })(importTarget);

    const fromToIdPairs = resolveNodeReferencesByPath({
      importableNode: portableSourceNode,
      destinationSlotPath,
      context: {
        destinationSlotPathByIdMap: slotPathByIdMap,
      },
    });

    const ambiguousLinkages: AmbiguousLinkage[] = [];
    const resolvedFromToIdPairs: [number, number][] = [];

    for (const [fromId, toId] of fromToIdPairs) {
      if (toId !== undefined) {
        resolvedFromToIdPairs.push([fromId, toId]);
      } else {
        const [, , context = [], basicNodeSpec] = externalNodeSlotPaths.find(([id]) => id === fromId) ?? [];

        if (basicNodeSpec) {
          ambiguousLinkages.push({ nodeId: fromId, destinationSlotPath, basicNodeSpec, context });
        }
      }
    }

    if (ambiguousLinkages.length > 0) {
      const resolvedLinkages = await onResolveAmbiguousLinkages?.(ambiguousLinkages);

      if (resolvedLinkages === undefined) throw new SlotPathError();

      resolvedFromToIdPairs.push(...resolvedLinkages);
    }

    const lookup = nodeImportLookup({ getNextId, fromToIdPairs: resolvedFromToIdPairs });
    const preprocessDesignerNodeForImport = preprocessDesignerNodeForImportCreate({ lookup, migrateSubtree });

    const generateDesignerNodeForImport = generateDesignerNodeForImportCreate({
      lookup,
      getNextId,
      getNodeTypeId,
      supportedLanguageCodes,
    });

    const [preprocessedSubjectNode, ...dereferencedNodes] = preprocessDesignerNodeForImport(originalNodes);

    // titles are settled before ids are remapped; dereferenced nodes are left alone, they take
    // their title from the layout node pointing at them (getLayoutCustomTitleForContentNode)
    const subjectNode = getTakenTitles
      ? renumberClashingTitles(preprocessedSubjectNode, getTakenTitles())
      : preprocessedSubjectNode;

    if (dereferencedNodes && addDereferencedNode) {
      for (const node of dereferencedNodes) {
        dereferencedNodesByOriginalId.set(
          node.id,
          generateDesignerNodeForImport(node, {
            // note we are passing the dereferencedNodesByOriginalId as we go along
            // so that dereferenced nodes can reference each other.
            // NOTE: will fail if referenced nodes are after referencing nodes (try to sort first?) or there is circular reference (shouldn't be allowed)
            dereferencedNodesByOriginalId,
          }),
        );
      }
      for (const node of dereferencedNodesByOriginalId.values()) {
        addDereferencedNode(node);
      }
    }

    return generateDesignerNodeForImport(subjectNode, {
      dereferencedNodesByOriginalId,
    });
  };
};

export type ImportDesignerNode = ReturnType<typeof importDesignerNodeCreate>;

const getTargetSlotPathCreate =
  ({
    getNodeTypeId,
    slotPathByIdMap,
  }: {
    getNodeTypeId: GetNodeTypeIdById;
    slotPathByIdMap: Map<number, NodeSlotPath>;
  }) =>
  (target: ImportTarget): NodeSlotPath => {
    if ("slotId" in target) {
      const { parentId, name, index } = target.slotId;
      const parentSlotPath = slotPathByIdMap.get(parentId);
      assert(parentSlotPath);
      const parentTypeId = getNodeTypeId(parentId);
      assert(parentTypeId);
      const slotSegment: NodeSlotPathSegment = {
        parent: [parentTypeId, parentId],
        name,
        ...(index ? { index } : undefined),
      };
      return [...parentSlotPath, slotSegment];
    } else {
      const slotPath = slotPathByIdMap.get(target.slotResidentNodeId);
      assert(slotPath);
      return slotPath;
    }
  };
